import React, { ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SideBannerRail from './side_banner_rail'
import useBanners from '../../hooks/useBanners'
import useMediaQuery from '../../hooks/useMediaQuery'
import { SIDE_RAIL_MEDIA_QUERY } from './banner_breakpoints'

/*
 * The page plus the two outer banner rails.
 *
 *   [ LEFT RAIL ]  [ header + hero + info row + game lists + footer ]  [ RIGHT RAIL ]
 *
 * Unlike Circus and Treasure — where the content column sits inside the page
 * and the rails are simply its siblings — Patek puts the WHOLE page (header,
 * main and footer) inside one centred `.container` in apps/_root.tsx. The free
 * outer space is therefore outside that container, so this wrapper goes around
 * it rather than inside it: the rails are siblings of `.container`, and the
 * container itself is not touched by a single pixel.
 *
 * The wrapper is always rendered, on every route and at every width, so the
 * tree below it never remounts when the viewport crosses the rail breakpoint.
 * Without `--with-rails` it carries no grid at all (see PHASE 25 in
 * circus.css): a plain block wrapper, and the page is exactly the layout it was
 * before — no columns, no reserved width, no extra margin.
 *
 * The banners come from the existing `/api/site/banners/` contract via
 * `useBanners`; `left` and `right` are rendered exactly as the API sends them.
 */
export default function PageRailsLayout({ children }: { children: ReactNode }) {
    const layoutRef = useRef<HTMLDivElement>(null)
    const { pathname } = useLocation()

    // The rails are desktop-only home-page furniture. The lobby and game-window
    // routes are full-bleed iframes with no outer space to fill, and below the
    // breakpoint the stylesheet hides the rails anyway — so gating the request
    // and the DOM on the same conditions means nothing is built, and no artwork
    // is ever requested, for a placement that cannot be shown.
    const railsFit = useMediaQuery(SIDE_RAIL_MEDIA_QUERY)
    const railsAllowed = pathname === '/' && railsFit

    const banners = useBanners(railsAllowed)
    const metrics = useRailMetrics(layoutRef, railsAllowed)

    const leftBanners = railsAllowed ? banners.left : []
    const rightBanners = railsAllowed ? banners.right : []
    const hasRails = leftBanners.length > 0 || rightBanners.length > 0
    // Explicit opt-in: tall stacks keep their natural size even with sticky enabled.
    const sticky = banners.sticky
    const style: React.CSSProperties = {}
    if (metrics) {
        Object.assign(style, {
            ['--rail-top' as any]: `${metrics.top}px`,
            ['--rail-tail' as any]: `${metrics.tail}px`,
        })
    }

    return (
        <div ref={layoutRef}
             className={`page-layout${hasRails ? ' --with-rails' : ''}${hasRails && sticky ? ' --sticky-rails' : ''}`}
             style={style}>
            <SideBannerRail side="left" banners={leftBanners} />
            {children}
            <SideBannerRail side="right" banners={rightBanners} />
        </div>
    )
}

interface RailMetrics {
    /** Hero top, measured from the top of `.page-layout`. */
    top: number
    /** Footer block: where the rail has to stop instead of trailing over it. */
    tail: number
}

/**
 * The rail geometry, measured off the real DOM instead of re-derived from the
 * stylesheet.
 *
 * Circus and Treasure can hardcode these numbers because their hero section is
 * built out of SCSS variables. Patek's header and hero have been retuned by
 * hand across two dozen appended PHASE blocks in circus.css, so a hardcoded
 * offset would be wrong the next time one of them moves. Measuring costs one
 * layout read on mount and keeps the rails level with the hero whatever the
 * header does.
 *
 * Every value has a CSS fallback (see PHASE 25), so a failed measurement
 * degrades to a sane layout rather than to a broken one.
 */
function useRailMetrics(ref: RefObject<HTMLDivElement>, active: boolean): RailMetrics | null {
    const [metrics, setMetrics] = useState<RailMetrics | null>(null)

    // Layout effect, not a plain one: the first measurement lands before the
    // browser paints, so the rails never appear at the fallback offset and then
    // jump to the measured one.
    useLayoutEffect(() => {
        const layout = ref.current
        if (!active || !layout) return

        function measure() {
            if (!layout) return
            const main = layout.querySelector('#main-container')
            const hero = layout.querySelector('#main-container .banner')
            const container = layout.querySelector(':scope > .container')
            if (!main || !hero || !container) return

            const layoutBox = layout.getBoundingClientRect()
            const mainBox = main.getBoundingClientRect()
            const heroBox = hero.getBoundingClientRect()
            const containerBox = container.getBoundingClientRect()

            const next: RailMetrics = {
                top: Math.round(heroBox.top - layoutBox.top),
                // A tall rail can extend the outer grid. Measure the footer
                // from the page column so the rail's own margin cannot feed
                // back into the next measurement and keep growing the page.
                tail: Math.max(0, Math.round(containerBox.bottom - mainBox.bottom)),
            }
            setMetrics(prev =>
                prev && prev.top === next.top && prev.tail === next.tail
                    ? prev
                    : next)
        }

        measure()
        window.addEventListener('resize', measure)
        window.addEventListener('load', measure)

        // The game lists arrive from the API long after mount and change how
        // tall the page is, which moves the footer — and with it the point the
        // rail has to stop at.
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
        observer?.observe(layout)
        // The page column can resize without changing a taller banner grid.
        for (const selector of [':scope > .container', '#root-header', '#main-container']) {
            const element = layout.querySelector(selector)
            if (element) observer?.observe(element)
        }

        return () => {
            window.removeEventListener('resize', measure)
            window.removeEventListener('load', measure)
            observer?.disconnect()
        }
    }, [ref, active])

    return metrics
}
